export interface BorderPoints {
  top: number
  right: number
  bottom: number
  left: number
}

export interface Point {
  x: number
  y: number
}

export const OFFSET = {
  playground: 15,
  object: -7.5,
  mag: 5,
}

export const degreesToRadians = (deg: number) => (deg * Math.PI) / 180.0
export const radiansToDegrees = (rad: number) => rad * (180.0 / Math.PI)

export const getPlaygroundValues = () => {
  const stage: HTMLDivElement | null = document.querySelector('.stage__main')
  const rect = stage?.getBoundingClientRect()
  const scale = stage?.style?.scale

  return {
    scale: scale ? Number(scale) : 1,
    offsetTop: rect?.top || 0,
    offsetLeft: rect?.left || 0,
    maxWidth: stage?.clientWidth || window.innerWidth,
    maxHeight: stage?.clientHeight || window.innerHeight
  }
}

export const getCursorShift = (initialPos: number, currentPos: number, scale: number) => (currentPos - initialPos) / scale

export const getRealPosition = (inital: number, offset: number, scale: number) => (inital - offset) / scale

/*
    explanation: https://www.mathsisfun.com/algebra/distance-2-points.html
*/
export const getDistanceAndRotateBetweenPoints = (p1: number[], p2: number[]) => {
  const a = p2[1] - p1[1]
  const b = p2[0] - p1[0]
  const distance = Math.sqrt((a * a) + (b * b))
  const rotate = radiansToDegrees(Math.atan2(a, b))

  return { distance, rotate }
}

export const calculateBorderPoints = (degree: number, width: number, height: number, x: number, y: number) => {
  const { hw, hh } = recalculateSize(degree, width, height)
  return {
    top: y - hh,
    right: x + hw,
    bottom: y + hh,
    left: x - hw
  }
}

export const getPointTransition = (rotation: number, translate: number) => {
  const radians = degreesToRadians(rotation)

  return {
    x: Math.cos(radians) * translate,
    y: Math.sin(radians) * translate
  }
}

export const calculateBoxSizeFromBorderPointsList = (list: BorderPoints[]) => {
  const { top, right, bottom, left } = list[0]
  const borderPoints = list.reduce((r, { top, right, bottom, left }) => {
    r.top = Math.min(r.top, top)
    r.right = Math.max(r.right, right)
    r.bottom = Math.max(r.bottom, bottom)
    r.left = Math.min(r.left, left)
    return r
  }, { top, right, bottom, left })

  const hw = (borderPoints.right - borderPoints.left) / 2.0
  const hh = (borderPoints.bottom - borderPoints.top) / 2.0

  return {
    hw,
    hh,
    x: borderPoints.left + hw,
    y: borderPoints.top + hh
  }
}

export const validatePosition = (value: number, min: number, max: number, addOffset = true, offset = 0) => {
  offset ||= addOffset ? OFFSET.playground : 0
  const minValue = min + offset
  const maxValue = max - offset
  return value < minValue ? minValue
    : value > maxValue ? maxValue
      : value
}

/*
    explanation: https://structx.com/Shape_Formulas_033.html
*/
export const recalculateSize = (degree: number, width: number, height: number) => {
  const radians = degreesToRadians(degree)
  const sin = Math.sin(radians)
  const cos = Math.cos(radians)

  const w = Math.abs(width * cos) + Math.abs(height * sin)
  const h = Math.abs(width * sin) + Math.abs(height * cos)

  return {
    w,
    h,
    hw: w / 2.0, // hw - half width
    hh: h / 2.0  // hh - half height
  }
}

/*
    explanation: https://en.wikipedia.org/wiki/Rotation_matrix#In_two_dimensions
*/
const rotatePoint = (point: Point, rotation: number) => {
  const radius = degreesToRadians(rotation)
  const sin = Math.sin(radius)
  const cos = Math.cos(radius)
  return {
    x: (point.x * cos) - (point.y * sin),
    y: (point.x * sin) + (point.y * cos)
  }
}

/*
    explanation: https://www.math-only-math.com/straight-line-in-two-point-form.html
*/
const calculateXpositionOfThirdPointOnStraightLinePassingThroughTwoPoints = ([p1, p2]: Point[], y = 0) => {
  const slope = (p2.y - p1.y) / (p2.x - p1.x)
  return ((y - p1.y) / slope) + p1.x
}

const findRightEdgeVertices = (vertices: Point[]) => {
  const [extremeRight, ...sortedVertices] = vertices.sort((a, b) => b.x - a.x)
  if (extremeRight.y === 0) return [extremeRight]

  const secondVertex = sortedVertices.find(p => extremeRight.y < 0 ? p.y > 0 : p.y < 0)
  return [extremeRight, secondVertex || sortedVertices[0]]
}

const calculateOffset = (vertices: Point[], width: number, height: number, newSize: number) => {
  const a = vertices[1].y - vertices[0].y
  const b = vertices[1].x - vertices[0].x
  const distance = Math.sqrt((a * a) + (b * b))
  const differenceWithWidth = Math.abs(width - distance)
  const differenceWithHeight = Math.abs(height - distance)
  const size = differenceWithHeight > differenceWithWidth ? height : width
  return (newSize / size) * OFFSET.mag
}

export const calculateDistanceToHorizontalEndOfRectangle = ({ width, height, rotation }: playground.Base_Object, withOffset = false) => {
  let distance = 0
  const halfWidth = width / 2.0
  const halfHeight = height / 2.0
  const vertices = [
    { x: -halfWidth, y: -halfHeight },
    { x: halfWidth, y: -halfHeight },
    { x: halfWidth, y: halfHeight },
    { x: -halfWidth, y: halfHeight },
  ]

  const rotatedVertices = vertices.map(p => rotatePoint(p, rotation))
  const rightEdgeVertices = findRightEdgeVertices(rotatedVertices)

  if (rightEdgeVertices.length === 1) {
    distance = rightEdgeVertices[0].x
    if (withOffset) distance += OFFSET.mag

  } else {
    distance = calculateXpositionOfThirdPointOnStraightLinePassingThroughTwoPoints(rightEdgeVertices)
    if (withOffset) distance += calculateOffset(rightEdgeVertices, width, height, distance * 2)
  }

  return distance
}